import { DatadogTrack } from '@cobliteam/datadog-rum';
import { type Camelized, camelize } from '@cobliteam/utils/camelize';
import type { z } from 'zod/v4';
import { ApiError } from './apiError';
import { authenticatedHeaders } from './authenticatedHeaders';
import { openTelemetryBaggageHeaders } from './baggage';

const UNAUTHORIZED = 401;
const NO_CONTENT = 204;

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type QueryPrimitive = string | number | boolean;
export type QueryParams = Record<string, QueryPrimitive | readonly QueryPrimitive[] | null | undefined>;

export type HttpRequest<TResult = unknown> = {
  method: HttpMethod;
  /** O host do serviço, declarado pelo domínio. */
  host: string;
  path: string;
  query?: QueryParams;
  /** Vai em JSON, menos `FormData`, que vai como multipart. */
  body?: unknown;
  signal?: AbortSignal;
  /** Repassado ao `camelize`, com os nomes originais em notação de ponto. */
  stopPaths?: readonly string[];
  /** Status que não vão ao Datadog, como o `nonReportableToDatadogErrors` do api-lib. */
  unreportedStatuses?: readonly number[];
  /** Contrato da resposta em snake_case, como sai do serviço. Validado antes do `camelize`. */
  schema?: z.ZodType<TResult>;
};

/** Código do `ApiError` quando a resposta não bate com o `schema` do endpoint. */
export const INVALID_RESPONSE = 'invalid-response';

// O mesmo `encode` do axios, para a query string sair igual à do api-lib: os mocks do e2e casam por ela.
const encode = (value: string) =>
  encodeURIComponent(value).replace(/%3A/gi, ':').replace(/%24/g, '$').replace(/%2C/gi, ',').replace(/%20/g, '+').replace(/%5B/gi, '[').replace(/%5D/gi, ']');

/** Serializa como o axios: `null` e `undefined` somem, e lista vira `chave[]=a&chave[]=b`. */
export const toQueryString = (query: QueryParams = {}) =>
  Object.entries(query)
    .flatMap(([key, value]) => {
      if (value === null || value === undefined) return [];
      if (Array.isArray(value)) return value.map((item) => `${encode(`${key}[]`)}=${encode(String(item))}`);
      return [`${encode(key)}=${encode(String(value))}`];
    })
    .join('&');

// EventSource e mensagem seguem o CobliAxios, para os monitores do Datadog continuarem casando.
const report = (error: ApiError, context: object = {}) => {
  DatadogTrack.error(error, {
    httpStatus: error.status,
    code: error.code,
    operation: error.operation,
    EventSource: 'CobliAxios',
    client: '@cobliteam/api',
    fingerprint: error.message,
    ...context,
  });
};

// Um endpoint divergente em produção reportaria a cada chamada; um aviso por rota e um teto por carga de página bastam.
const MAX_SCHEMA_REPORTS = 20;
const reportedRoutes = new Set<string>();
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Só para testes: libera de novo os avisos de divergência já mandados. */
export const resetSchemaReports = () => reportedRoutes.clear();

// ADR-0021, item 8: estrito em dev e no e2e, onde a divergência precisa quebrar; em produção, a tela segue com o dado como veio.
const isStrict = () => import.meta.env.DEV || (typeof navigator !== 'undefined' && navigator.webdriver);

const validate = <TResult>(schema: z.ZodType<TResult>, data: unknown, method: HttpMethod, path: string, status: number) => {
  const result = schema.safeParse(data);
  // O dado sai como veio, e não o `result.data`: o `z.object` removeria o campo que o contrato ainda não descreve.
  if (result.success) return data;

  const operation = `${method} ${path}`;
  // Caminho e código do problema, sem o valor recebido, que pode ser dado do cliente.
  const issues = result.error.issues.slice(0, 10).map((issue) => ({ path: issue.path.join('.'), code: issue.code, message: issue.message }));
  const error = new ApiError({
    message: `HTTP ${operation} returned a body outside the contract - ${issues.map((issue) => `${issue.path || '(raiz)'}: ${issue.message}`).join('; ')}`,
    origin: 'http',
    status,
    code: INVALID_RESPONSE,
    operation,
    cause: result.error,
  });
  if (isStrict()) throw error;

  const route = `${method} ${path.replace(UUID, ':id')}`;
  if (!reportedRoutes.has(route) && reportedRoutes.size < MAX_SCHEMA_REPORTS) {
    reportedRoutes.add(route);
    report(error, { route, issues, fingerprint: `invalid-response ${route}` });
  }
  return data;
};

const readJson = async (response: Response): Promise<unknown> => {
  if (response.status === NO_CONTENT) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const bodyMessage = (body: unknown): { message?: string; code?: string } => {
  if (!body || typeof body !== 'object') return {};
  const { message, error, detail, code, errorCode, error_code } = body as Record<string, unknown>;
  const text = [message, error, detail].find((value) => typeof value === 'string') as string | undefined;
  const errorCodeValue = [code, errorCode, error_code].find((value) => typeof value === 'string') as string | undefined;
  return { message: text, code: errorCodeValue };
};

/**
 * Faz uma chamada REST autenticada e devolve o corpo em camelCase, ou `undefined` quando não há
 * corpo. Com `schema`, o corpo é validado antes (ver `validate`). Status fora de 2xx vira `ApiError`. O cancelamento pelo `signal` rejeita com o próprio
 * `AbortError`, que o TanStack Query reconhece.
 */
export const requestHttp = async <TResult = unknown>({
  method,
  host,
  path,
  query,
  body,
  signal,
  stopPaths,
  unreportedStatuses = [],
  schema,
}: HttpRequest<TResult>): Promise<Camelized<TResult>> => {
  const queryString = toQueryString(query);
  const url = `${host}${path}${queryString ? `?${queryString}` : ''}`;
  const operation = `${method} ${path}`;
  const isFormData = body instanceof FormData;
  const headers = {
    ...(await authenticatedHeaders(operation, (error) => report(error))),
    'cobli-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...openTelemetryBaggageHeaders(),
    // No multipart, o navegador escreve o `Content-Type` com o boundary; um valor fixo aqui quebraria o corpo.
    ...(isFormData ? {} : { 'Content-type': 'application/json' }),
  };

  let response: Response;
  try {
    response = await fetch(url, { method, headers, body: body === undefined || isFormData ? (body as FormData | undefined) : JSON.stringify(body), signal });
  } catch (cause) {
    if (signal?.aborted) throw cause;
    // Sem resposta, o CobliAxios não reporta; o erro de rede só volta para quem chamou.
    throw new ApiError({ message: `Network error for ${method} ${url} - ${(cause as Error).message}`, origin: 'http', operation, cause });
  }

  const data = await readJson(response).catch((cause) => {
    if (signal?.aborted) throw cause;
    return undefined;
  });

  if (!response.ok) {
    const detail = bodyMessage(data);
    const error = new ApiError({
      message: `HTTP ${method} ${url} failed - ${response.status}${detail.message ? `: ${detail.message}` : ''}`,
      origin: 'http',
      status: response.status,
      code: detail.code,
      operation,
      cause: data,
    });
    // Como no CobliAxios, o 401 só recarrega a página (e volta ao login) quando também é reportado.
    if (!unreportedStatuses.includes(response.status)) {
      report(error, { fullURL: url });
      if (response.status === UNAUTHORIZED) window.location.reload();
    }
    throw error;
  }

  const valid = schema ? validate(schema, data, method, path, response.status) : data;
  if (valid === null || typeof valid !== 'object') return valid as Camelized<TResult>;
  return camelize(valid as Record<string, unknown>, { stopPaths }) as Camelized<TResult>;
};
